import { CampaignType, recordListUserInfo, DISPLAY_COUPON_TYPES, EarningRuleGlobalLimitReward } from 'config/CustomEnums';
import { parseExpiredDate } from 'models/CouponListModel';
import { loading } from 'models/LoadingUtil';
import {
  getCouponTransaction,
  getCouponTransactionReports,
  getCouponTransactions,
  getTicketTransactionReports,
} from 'services/TransactionRecordsAPIHelper';

import {
  convertCursorToNumber,
  convertNumberToCursor,
  createAction,
  formatNumberWithCommas,
} from 'utils';
import { formatDate, getDetailPageDisplayLastModifiedDate, getDisplayDate, getListDisplayLastModifiedDate } from 'utils/TimeFormatUtil';
import { capitalizeFirstLetter } from 'utils/StringUtil';
import { parseTransactionDisplayType, transactionDisplayInvoiceIdAndId } from './TransactionModel';
import { parseReachLimitInfo } from 'utils/ReachLimitInfoUtils';

import { 
  getCampaignDisplayName,
  getCampaignLink,
} from 'models/RecordUtils';

const getInitialState = () => ({
  couponTransactionList: [],
  listDisplayFields: [
    { displayName: 'ID', fieldName: 'pk', linked: true },
    ...recordListUserInfo,
    { displayName: 'Record Type', fieldName: 'displayType' },
    { displayName: 'Reference ID', fieldName: 'couponReferenceId'},
    { displayName: 'Coupon ID', fieldName: 'couponID', orderField: 'coupon' },
    {
      displayName: 'Coupon Set Name',
      fieldName: 'couponTemplateName',
      orderField: 'couponTemplateName',
    },
    {
      displayName: 'Expiry Date',
      fieldName: 'couponExpiryDate',
    },
    { displayName: 'Purchase date', fieldName: 'purchaseDate', orderField: "triggerTime" },
    { displayName: 'Creation date', fieldName: 'creationDate', orderField: "creationDate" },
  ],
  ticketListDisplayFields: [
    { displayName: 'ID', fieldName: 'pk', linked: true },
    ...recordListUserInfo,
    { displayName: 'Record Type', fieldName: 'displayType' },
    { displayName: 'Reference ID', fieldName: 'couponReferenceId' },
    { displayName: 'Ticket ID', fieldName: 'couponID', orderField: 'coupon' },
    {
      displayName: 'Ticket Set Name',
      fieldName: 'couponTemplateName',
      orderField: 'couponTemplateName',
    },
    {
      displayName: 'Expiry Date',
      fieldName: 'couponExpiryDate',
    },
    { displayName: 'Purchase date', fieldName: 'purchaseDate', orderField: "triggerTime" },
    { displayName: 'Creation date', fieldName: 'creationDate', orderField: "creationDate" },
  ],
  reportListDisplayFields: [
    { displayName: 'ID', fieldName: 'pk' },
    { displayName: 'Name', fieldName: 'name', linked: false },
    { displayName: 'Report type', fieldName: 'reportType' },
    { displayName: 'Time range', fieldName: 'displayTimeRange' },
    { displayName: 'Creation time', fieldName: 'creationDate' },
  ],
  pageInfo: {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  currentLastCursor: '',
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
  checkedList: [],
  currentPageCouponTransactionList: [],
  selectedCouponTransaction: {},
  currentPageCouponTransactionReportList: [],
  currentPageticketTransactionReportList: [],
});

const parseCouponTransactionType = (type) => {
  let displayType = '';
  switch (type) {
    case 'EARN':
      displayType = 'Customer earned';
      break;
    case 'BUY':
      displayType = 'Customer acquired';
      break;
    case 'USE':
      displayType = 'Customer used';
      break;
    case 'GRANT':
      displayType = 'Admin added';
      break;
    case 'RECLAIM':
      displayType = 'Admin removed';
      break;
    case 'ADMIN_USE':
      displayType = 'Admin used';
      break;
    case 'MERCHANT_GRANT':
      displayType = 'Merchant added';
      break;
    case 'MERCHANT_RECLAIM':
      displayType = 'Merchant removed';
      break;
    case 'MERCHANT_USE':
      displayType = 'Merchant used';
      break;
    case 'EXPIRED':
      displayType = 'Coupon expired';
      break;
    case 'DEACTIVATE':
      displayType = 'Admin deactivated';
      break;
    case 'REACTIVATE':
      displayType = 'Admin reactivated';
      break;
    case 'RETURN':
      displayType = 'Return';
      break;
    case 'ADMIN_TRANSFERRED':
      displayType = 'Admin transferred';
      break
    default:
      break;
  }
  return displayType;
};

const parseCouponReportType = (reportType) => {
  switch (reportType) {
    case 'WEEKLY':
      return 'Weekly';
    case 'MONTHLY':
      return 'Monthly';
    default:
      break;
  }
}

const getEventType = (transactionType, isTicket=false) => {
  let eventType = '';
  switch (transactionType) {
    case 'EARN':
    case 'BUY':
    case 'GRANT':
    case 'MERCHANT_GRANT':
    case 'REACTIVATE':
    case 'RETURN':
    case 'ADMIN_TRANSFERRED':
      eventType = isTicket ? 'Add Ticket' : 'Add Coupon';
      break;
    case 'USE':
    case 'RECLAIM':
    case 'ADMIN_USE':
    case 'MERCHANT_RECLAIM':
    case 'MERCHANT_USE':
    case 'EXPIRED':
    case 'DEACTIVATE':
      eventType = isTicket ? 'Remove Ticket' : 'Remove Coupon';
      break;
    default:
      break;
  }
  return eventType;
};

const parseCouponTransactionLinkedTransaction = (transaction) => {
  if (!transaction) {
    return {};
  }
  const transactionType = transaction?.transactionType
  return {
    pk: transaction?.pk,
    transactionType,
    transactionDisplayType: parseTransactionDisplayType(transaction.transactionType),
    posInvoiceId: transaction?.posInvoiceId,
    invoiceId: transaction?.invoiceId,
    totalValue: transaction.totalValue,
    displayTotalValue: transaction.totalValue
      ? `HK$ ${formatNumberWithCommas(transaction.totalValue)}`
      : '',
    paidAmount: transaction.paidAmount,
    displayPaidAmount: transaction.paidAmount
      ? `HK$ ${formatNumberWithCommas(transaction.paidAmount)}`
      : '',
    transactionDetailDisplayDate: getDetailPageDisplayLastModifiedDate(
      transaction.date
    ),
    transactionDisplayInvoiceInfo: transactionDisplayInvoiceIdAndId(transaction),
    detailDisplayPurchaseDate: getDetailPageDisplayLastModifiedDate(
      transaction.purchasedDate
    ),
  }
}


const parseCouponTransaction = (item) => {
  const earningRule = item.earningRule;
  const campaign = item.campaign || earningRule?.campaigns?.edges?.[0]?.node;
  const isStampCampaign = item.campaign?.type === CampaignType.stampCampaign;
  const isTicketCampaign = item.campaign?.type === CampaignType.ticketCampaign;
  return {
    pk: item.pk,
    id: item.id,
    customerId: item.customer?.pk,
    membershipId: item.customer?.membershipId,
    mobilePhoneNumber: item.customer?.mobilePhoneNumberSubscriberNumber
    ? `+${item.customer.mobilePhoneNumberCountryCode}\r\n${item.customer.mobilePhoneNumberSubscriberNumber}` : null,
    email: item.customer?.emailAddress,
    customerIsRestricted: item.customer?.isReset || item.customer?.isTransferred,
    ssoUid: item.customer?.ssoUid,
    originType: item.type,
    displayType: parseCouponTransactionType(item.type),
    pointsSpent: item.pointsSpent,
    creationDate: getListDisplayLastModifiedDate(item.creationDate),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(item.creationDate),
    purchaseDate: getListDisplayLastModifiedDate(item.triggerTime),
    detailDisplayPurchaseDate: getDetailPageDisplayLastModifiedDate(item.triggerTime),
    usedDate: formatDate(item.usedAt, 'DD MMM yyyy (ddd), HH:mm a'),
    name: item.customer
      ? item.customer?.nickname
        ? `${item.customer?.firstName} ${item.customer?.lastName} (${item.customer?.nickname})`
        : `${item.customer?.firstName} ${item.customer?.lastName}`
      : null,
    isStampCampaign,
    campaignName: campaign?.name,
    earningRuleName: earningRule?.name,
    couponID: item.coupon?.pk,
    couponTemplateName: item.coupon?.template?.name,
    couponTemplateNameWithSerialNumber: `${capitalizeFirstLetter(
      item.type.toString().toLowerCase(),
    )} ${item.coupon?.template?.name}(${item.coupon?.serialNumber})`,
    couponType: item.coupon?.template?.couponType,
    displayCouponType: DISPLAY_COUPON_TYPES[item.coupon?.template?.couponType],
    couponReferenceId:item.coupon?.referenceId,
    couponExpiryDate: item.coupon?.template
      ? parseExpiredDate(item.coupon?.template)
      : null,
    administratorName: item?.administrator?.username,
    displayAdministrator: item.administrator?.pk
    ? `[ID: ${item.administrator?.pk}] ${item.administrator?.username}`
    : item.administrator?.username,
    createdDate: formatDate(item.creationDate),
    eventType: getEventType(item.type, isTicketCampaign),
    displayCampaign: getCampaignDisplayName(campaign),
    campaignLink: getCampaignLink(campaign),
    displayCoupon: item.coupon
      ? `[ID: ${item.coupon?.pk}] ${item.coupon?.template?.name}`
      : '-',
    displayEarningRule: earningRule
      ? `[ID: ${earningRule?.pk}] ${earningRule?.name}`
      : '-',
    transaction: parseCouponTransactionLinkedTransaction(item.transaction),
    reachLimit: item.reachLimit,
    reachLimitInfo: parseReachLimitInfo(
      item.reachLimitInfo,
      item.allReachLimitInfo,
      EarningRuleGlobalLimitReward.COUPONS
    ),
    remarks: item.remarks,
    displayCouponTemplateInfo: item?.coupon?.template?.pk || '-'
  };
};

const parseCouponTransactionReports = (item) => {
  return {
    pk: item.pk,
    id: item.id,
    name: item.reportName,
    reportType: parseCouponReportType(item.reportType),
    displayTimeRange: `${formatDate(item.startTime, 'DD MMM yyyy')} - ${formatDate(item.endTime, 'DD MMM yyyy')}`,
    creationDate: formatDate(item.creationDate, 'DD MMM yyyy'),
    reportFileUrl: item.reportFileUrl,
  };
};

export default {
  namespace: 'couponTransactions',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    clearData(state, { payload }) {
      return {
        ...state,
        ...getInitialState(),
      };
    },
  },
  effects: {
    getCouponTransactions: [
      function* ({ payload }, { call, put }) {
        // const { afterCursor } = payload;
        const serviceArgs = [
          getCouponTransactions,
          null,
          { ...payload, rank: true },
        ];
        function* onSuccess(data) {
          const conponTransactionData = data.couponTransactions.edges;
          const couponTransactionList = conponTransactionData.map((item) =>
            parseCouponTransaction(item.node),
          );
          yield put(
            createAction('updateState')({
              couponTransactionList: couponTransactionList,
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getCurrentPageCouponTransactions: [
      function* ({ payload }, { call, put }) {
        const { page } = payload;
        let afterCursor = undefined;
        if (page > 1) {
          afterCursor = btoa(`arrayconnection:${(page - 1) * 20 - 1}`);
        }
        console.log('@@158: ', afterCursor);
        const serviceArgs = [getCouponTransactions, afterCursor, payload];
        function* onSuccess(data) {
          const conponTransactionData = data.couponTransactions.edges;
          const pageInfo = data.couponTransactions.pageInfo;
          const totalCount = data.couponTransactions.totalCount;
          const currentLastCursor = pageInfo.endCursor;
          const couponTransactionList = conponTransactionData.map((item) =>
            parseCouponTransaction(item.node),
          );
          yield put(
            createAction('updateState')({
              currentPageCouponTransactionList: couponTransactionList,
              pageInfo: {
                startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
              },
              currentLastCursor,
              totalCount,
              totalPage: Math.ceil(totalCount / 20),
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getSingleCouponTransactionRecord: [
      function* ({ payload }, { put }) {
        const { couponTransactionPK } = payload;
        const transactionID = btoa(
          `CouponTransactionNode:${couponTransactionPK}`,
        );
        const serviceArgs = [getCouponTransaction, transactionID];
        function* onSuccess(data) {
          yield put(
            createAction('updateState')({
              selectedCouponTransaction: parseCouponTransaction(
                data.couponTransaction,
              ),
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getCurrentPageCouponTransactionsReports: [
      function* ({ payload }, { call, put }) {
        const { page } = payload;
        let afterCursor = undefined;
        if (page > 1) {
          afterCursor = convertNumberToCursor((page - 1) * 20 - 1);
        }
        const serviceArgs = [getCouponTransactionReports, afterCursor, payload];
        function* onSuccess(data) {
          const conponTransactionReportsData = data.couponReports.edges;
          const pageInfo = data.couponReports.pageInfo;
          const totalCount = data.couponReports.totalCount;
          const currentLastCursor = pageInfo.endCursor;
          const couponTransactionReportList = conponTransactionReportsData.map((item) =>
            parseCouponTransactionReports(item.node),
          );
          yield put(
            createAction('updateState')({
              currentPageCouponTransactionReportList: couponTransactionReportList,
              pageInfo: {
                startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
              },
              currentLastCursor,
              totalCount,
              totalPage: Math.ceil(totalCount / 20),
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getCurrentPageTicketTransactionsReports: [
      function* ({ payload }, { call, put }) {
        const { page } = payload;
        let afterCursor = undefined;
        if (page > 1) {
          afterCursor = convertNumberToCursor((page - 1) * 20 - 1);
        }
        const serviceArgs = [getTicketTransactionReports, afterCursor, payload];
        function* onSuccess(data) {
          const ticketTransactionReportsData = data.ticketReports.edges;
          const pageInfo = data.ticketReports.pageInfo;
          const totalCount = data.ticketReports.totalCount;
          const currentLastCursor = pageInfo.endCursor;
          const ticketTransactionReportList = ticketTransactionReportsData.map((item) =>
            parseCouponTransactionReports(item.node),
          );
          yield put(
            createAction('updateState')({
              currentPageticketTransactionReportList: ticketTransactionReportList,
              pageInfo: {
                startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
              },
              currentLastCursor,
              totalCount,
              totalPage: Math.ceil(totalCount / 20),
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
};
